/** The organisation page's "Plan & limits" card: plan, verification tier, the switch program (the competitor the
 * company moves from; staff approve or reject), extra seats, and every limit for this company: its own value over
 * the plan's (custom limits, enterprise contract values) and its own enforcement switch (inherit, enforced, not
 * enforced). PATCH /v1/admin/orgs/{id}/plan, audited `organization.plan` and `organization.switch_decision`. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Plus, Save, X } from "lucide-react";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Badge, Button, Card, Field, NumberInput, Select, Spinner, useToast } from "../../components/ui";
import { enforcementOf, formatValue, fromInput, switchBonuses, type Enforcement } from "../../lib/plans";
import { useIntlShape, useT } from "../../i18n/I18nProvider";
import { useFmt } from "./format";

export function useOrgPlan(orgId: string, admin: boolean) {
  return useQuery({
    queryKey: ["org-plan", orgId, admin],
    queryFn: () =>
      admin
        ? unwrap(api.GET("/v1/admin/orgs/{org_id}/plan", { params: { path: { org_id: orgId } } }))
        : unwrap(api.GET("/v1/orgs/{org_id}/plan", { params: { path: { org_id: orgId } } })),
  });
}

const SWITCH_TONE = { requested: "warn", approved: "ok", rejected: "danger" } as const;

export function OrgPlan({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const intlLocale = useIntlShape().locale;
  const { lux, num } = useFmt();
  const current = useOrgPlan(orgId, true);
  const [plan, setPlan] = useState<S["PlanKey"]>("free");
  const [verification, setVerification] = useState<S["VerificationTier"]>("unverified");
  const [extraSeats, setExtraSeats] = useState<number | null>(0);
  const [itemKey, setItemKey] = useState("lux_month");
  const [itemText, setItemText] = useState("");
  const [competitor, setCompetitor] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!current.data) return;
    setPlan(current.data.plan);
    setVerification(current.data.verification);
    setExtraSeats(current.data.extra_seats);
    setCompetitor(current.data.switch?.competitor ?? "");
  }, [current.data]);
  const patch = useMutation({
    mutationFn: (body: S["OrganizationPlanWrite"]) =>
      unwrap(api.PATCH("/v1/admin/orgs/{org_id}/plan", { params: { path: { org_id: orgId } }, body })),
    onSuccess: (data) => {
      qc.setQueryData(["org-plan", orgId, true], data);
      qc.invalidateQueries({ queryKey: ["admin-org", orgId] });
      qc.invalidateQueries({ queryKey: ["lux", orgId] });
      toast(t("admin.common.saved"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (!current.data) return <Card title={t("admin.orgPlan.title")}>{current.isLoading ? <Spinner /> : errorMessage(current.error)}</Card>;
  const d = current.data;
  const items = d.catalogue?.items ?? [];
  const planValues = d.catalogue?.plans.find((p) => p.key === d.plan);
  const item = items.find((i) => i.key === itemKey);
  const parsed = item ? fromInput(item, itemText) : undefined;
  const u = d.usage;
  const sw = d.switch;
  const dirty = plan !== d.plan || verification !== d.verification || (extraSeats ?? 0) !== d.extra_seats;
  const decide = (status: "approved" | "rejected" | "none") =>
    patch.mutate({ switch: { status, competitor: competitor.trim() || null, note: note.trim() || null } });
  const setEnforcement = (key: string, to: Enforcement) =>
    patch.mutate(to === "inherit" ? { inherit: [key] } : to === "off" ? { disable: [key] } : { enable: [key] });
  return (
    <Card title={t("admin.orgPlan.title")} description={t("admin.orgPlan.description")}>
      <div className="stack">
        <div className="form-grid">
          <Field label={t("admin.orgPlan.plan")}>
            <Select value={plan} onChange={setPlan} options={(d.catalogue?.plans ?? []).map((p) => ({ value: p.key, label: p.name }))} />
          </Field>
          <Field label={t("admin.orgPlan.verification")} hint={t("admin.orgPlan.verificationHint")}>
            <Select
              value={verification}
              onChange={setVerification}
              options={(["unverified", "domain", "business"] as const).map((tier) => ({ value: tier, label: t("admin.orgPlan.verificationTier", { tier }) }))}
            />
          </Field>
          <Field label={t("admin.orgPlan.extraSeats")} hint={t("admin.orgPlan.extraSeatsHint")}>
            <NumberInput value={extraSeats} min={0} step={1} onChange={setExtraSeats} />
          </Field>
        </div>
        <div>
          <Button
            variant="primary"
            icon={<Save />}
            disabled={!dirty}
            loading={patch.isPending}
            onClick={() => patch.mutate({ plan, verification, extra_seats: Math.max(0, Math.round(extraSeats ?? 0)) })}
          >
            {t("admin.orgPlan.savePlan")}
          </Button>
        </div>
        <div className="muted small">
          {t(u.lux_bonus_designs ? "admin.orgPlan.usageLuxWithBonus" : "admin.orgPlan.usageLux", {
            granted: lux(u.lux_reserved_granted + u.lux_free_granted),
            designLeft: lux(u.lux_reserved_left),
            monthlyLeft: lux(u.lux_free_left),
            bonus: lux(u.lux_bonus_designs),
            purchased: lux(u.lux_purchased),
          })}{" "}
          ·{" "}
          {u.seats === null || u.seats === undefined
            ? t("admin.orgPlan.usageSeatsContract", { used: u.seats_used })
            : t("admin.orgPlan.usageSeats", { used: u.seats_used, limit: num(u.seats) })}{" "}
          ·{" "}
          {t("admin.orgPlan.usageCounts", { viewers: u.viewers, websites: u.websites, widget: num(u.widget_searches_month), designer: num(u.designer_searches_month) })}
        </div>

        <div className="plan-switch">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <b>{t("admin.orgPlan.switchProgram")}</b>
            {sw ? <Badge tone={SWITCH_TONE[sw.status]}>{t("admin.orgPlan.switchStatus", { status: sw.status })}</Badge> : <Badge plain>{t("admin.orgPlan.noRequest")}</Badge>}
          </div>
          <p className="muted small" style={{ marginBlock: "4px 8px", marginInline: 0 }}>
            {t("admin.orgPlan.switchHelp", { bonuses: switchBonuses(d.catalogue?.plans ?? [], intlLocale) })}
          </p>
          {(sw?.evidence || sw?.decision_note) && (
            <div className="small" style={{ marginBottom: 8 }}>
              {sw.evidence && (
                <div>
                  <FormattedMessage id="admin.orgPlan.evidence" values={{ evidence: <span dir="auto">{sw.evidence}</span>, label: (c) => <span className="muted">{c}</span> }} />
                </div>
              )}
              {sw.decision_note && (
                <div>
                  <FormattedMessage id="admin.orgPlan.decisionNote" values={{ note: <span dir="auto">{sw.decision_note}</span>, label: (c) => <span className="muted">{c}</span> }} />
                </div>
              )}
            </div>
          )}
          <div className="form-grid">
            <Field label={t("admin.orgPlan.switchingFrom")}>
              <input className="input" dir="auto" value={competitor} maxLength={100} onChange={(e) => setCompetitor(e.target.value)} placeholder={t("admin.orgPlan.switchingFromPlaceholder")} />
            </Field>
            <Field label={t("admin.orgPlan.decisionNoteLabel")}>
              <input className="input" dir="auto" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder={t("admin.orgPlan.decisionNotePlaceholder")} />
            </Field>
          </div>
          <div className="row" style={{ marginTop: 8 }}>
            <Button icon={<Check />} disabled={!competitor.trim() || sw?.status === "approved"} onClick={() => decide("approved")}>
              {t("admin.orgPlan.approve")}
            </Button>
            <Button variant="ghost" icon={<X />} disabled={!sw || sw.status === "rejected"} onClick={() => decide("rejected")}>
              {t("admin.orgPlan.reject")}
            </Button>
            {sw && (
              <Button variant="ghost" onClick={() => decide("none")}>
                {t("admin.orgPlan.clear")}
              </Button>
            )}
          </div>
        </div>

        <div>
          <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>
            {t("admin.orgPlan.setValueTitle", { plan: d.plan_name })}
          </div>
          <div className="form-grid">
            <Field label={t("admin.orgPlan.item")}>
              <Select value={itemKey} onChange={setItemKey} options={items.filter((i) => !i.key.endsWith("_price_cents")).map((i) => ({ value: i.key, label: `${i.area} · ${i.label}` }))} />
            </Field>
            <Field label={t("admin.orgPlan.value")} hint={item ? t("admin.orgPlan.valueHint", { plan: formatValue(item, planValues?.values[item.key]), none: item.none_label }) : undefined}>
              <input className="input" value={itemText} onChange={(e) => setItemText(e.target.value)} placeholder={item?.none_label} />
            </Field>
          </div>
          <div style={{ marginTop: 8 }}>
            <Button
              icon={<Plus />}
              disabled={parsed === undefined}
              onClick={() => {
                patch.mutate({ overrides: { [itemKey]: parsed } });
                setItemText("");
              }}
            >
              {t("admin.orgPlan.setValue")}
            </Button>
          </div>
        </div>

        <details open>
          <summary className="small" style={{ fontWeight: 600, cursor: "pointer" }}>
            {t("admin.orgPlan.allLimits", { disabled: d.disabled.length, own: Object.keys(d.overrides).length })}
          </summary>
          <div className="table-wrap" style={{ marginTop: 8 }}>
            <table className="table org-limits">
              <thead>
                <tr>
                  <th>{t("admin.orgPlan.colLimit")}</th>
                  <th>{t("admin.orgPlan.colPlan")}</th>
                  <th>{t("admin.orgPlan.colCompany")}</th>
                  <th>{t("admin.orgPlan.colEnforced")}</th>
                  <th>{t("admin.orgPlan.colEffective")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => {
                  const own = i.key in d.overrides;
                  const enforcement = enforcementOf(d.enforcement, i.key);
                  return (
                    <tr key={i.key}>
                      <td>
                        <div className="small">{i.label}</div>
                        <div className="faint small mono i18n-ltr">{i.key}</div>
                      </td>
                      <td className="small">
                        {formatValue(i, planValues?.values[i.key])}
                        {planValues?.disabled.includes(i.key) && <div className="faint small">{t("admin.orgPlan.offOnPlan")}</div>}
                      </td>
                      <td className="small">
                        {own ? (
                          <span className="row" style={{ gap: 4, flexWrap: "nowrap" }}>
                            <Badge tone="info" plain>{formatValue(i, d.overrides[i.key])}</Badge>
                            <button className="icon-btn" aria-label={t("admin.orgPlan.backToPlanItem", { item: i.label })} title={t("admin.orgPlan.backToPlan")} onClick={() => patch.mutate({ clear: [i.key] })}>
                              <X size={14} />
                            </button>
                          </span>
                        ) : (
                          <span className="faint">{t("admin.orgPlan.plansValue")}</span>
                        )}
                      </td>
                      <td>
                        {i.can_disable ? (
                          <Select<Enforcement>
                            value={enforcement}
                            ariaLabel={t("admin.orgPlan.enforcementOf", { item: i.label })}
                            onChange={(v) => setEnforcement(i.key, v)}
                            options={[
                              { value: "inherit", label: t("admin.orgPlan.asPlan") },
                              { value: "on", label: t("admin.orgPlan.enforced") },
                              { value: "off", label: t("admin.orgPlan.notEnforced") },
                            ]}
                          />
                        ) : (
                          <span className="faint small">{t("admin.orgPlan.always")}</span>
                        )}
                      </td>
                      <td className="small">
                        {d.disabled.includes(i.key) ? <Badge tone="warn" plain>{t("admin.orgPlan.notEnforcedBadge")}</Badge> : formatValue(i, d.values[i.key])}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      </div>
    </Card>
  );
}
